import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FC } from 'react';

import { IcBackIcon, IcForwardStrokeIcon, IcSDeleteIcon, IcToggleDownIcon } from '@gem/icons';
import { IconButton, IconViewSizes } from '@gem/ui-kit';

import { IMediaGalleryState } from 'APP/types/mediaGallery';

import {
  IMediaGalleryFile,
  TSetSidebarVisabilityHandler,
  TCloseHandler,
} from '../MediaGallery.types';
import { MediaGalleryDownload } from './MediaGalleryDownloadState/MediaGalleryDownload';
import { useMediaGalleryHeaderPresenter } from './MediaGalleryHeader.presenter';
import styles from './MediaGalleryHeader.style.m.css';
import { MediaGalleryInfoCard } from './MediaGalleryInfoCard/MediaGalleryInfoCard';

interface IMediaGalleryHeaderPresenterParams {
  file: IMediaGalleryFile;
  canDelete: boolean;
  canNavigate: boolean;
  canOpenSidebar: boolean;
  isShowSidebar: boolean;
  onSetSidebarVisibility: TSetSidebarVisabilityHandler;
  onClose: TCloseHandler;
  galleryState: IMediaGalleryState;
}

export const MediaGalleryHeader: FC<IMediaGalleryHeaderPresenterParams> = observer((props) => {
  const {
    file,
    canDelete,
    canNavigate,
    canOpenSidebar,
    isShowSidebar,
    onSetSidebarVisibility,
    onClose,
    galleryState,
  } = props;

  const presenter = useMediaGalleryHeaderPresenter({
    file,
    canDelete,
    isShowSidebar,
    onSetSidebarVisibility,
    galleryState,
  });

  const sidebarButtonClassName = classNames(styles.control, {
    [styles.rotatedControl]: isShowSidebar,
  });

  if (!presenter.message || !presenter.sender || !presenter.payloadId) {
    return null;
  }

  return (
    <div className={styles.container}>
      <IconButton
        Icon={IcBackIcon}
        size={IconViewSizes.Big}
        iconSize={IconViewSizes.Medium}
        theme="11"
        onClick={onClose}
      />

      <div className={styles.rightPanel}>
        <div className={styles.controls}>
          {presenter.canBeDownload && (
            <MediaGalleryDownload
              className={styles.control}
              message={presenter.message}
              payloadId={presenter.payloadId}
            />
          )}

          {presenter.canBeDelete && (
            <>
              <IconButton
                className={styles.control}
                Icon={IcSDeleteIcon}
                size={IconViewSizes.Big}
                iconSize={IconViewSizes.Medium}
                theme="11"
                onClick={presenter.onDelete}
              />
            </>
          )}

          {presenter.canBeForward && (
            <IconButton
              className={styles.control}
              Icon={IcForwardStrokeIcon}
              size={IconViewSizes.Big}
              iconSize={IconViewSizes.Medium}
              theme="11"
              onClick={presenter.onForward}
            />
          )}
        </div>

        <div className={styles.messageInfoPanel}>
          {!isShowSidebar && <div className={styles.divider} />}

          <MediaGalleryInfoCard
            message={presenter.message}
            sender={presenter.sender}
            canNavigate={canNavigate}
            onClose={onClose}
          />

          {canOpenSidebar && (
            <IconButton
              className={sidebarButtonClassName}
              Icon={IcToggleDownIcon}
              size={IconViewSizes.Big}
              iconSize={IconViewSizes.Medium}
              theme="12"
              onClick={presenter.onToggleSidebar}
            />
          )}
        </div>
      </div>
    </div>
  );
});
